import { createReducer, on } from '@ngrx/store';
import {
  Address,
  Amenity,
  CardType,
  CustomerServiceNoteDetail,
  Driver,
  FavoriteLocation,
  Itinerary,
  Organization,
  OrganizationPaymentMethod,
  OrganizationRider,
  OrganizationRiderDetail,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  Payment,
  PaymentAccount,
  Provider,
  ReservationChargeDetail,
  RideLimitDetail,
  RideMetadataField,
  Rider,
  RideRequest,
  RideRequestType,
  RideSubscriptionDetail,
  RideUtil,
  Transaction,
  Trip,
  VehicleType
} from '@uzurv/clara';
import { isNullOrUndefined, ServiceLevelConstants, ServiceLevelType, VehicleTypeConstants } from '@uzurv/core';
import { cloneDeep, findIndex } from 'lodash';
import { PaymentCollectionType } from 'projects/sdk/packages/clara/src/models/payment-collection-type';
import * as OrganizationRiderActions from './organization-rider.actions';

export interface OrganizationRiderState {
  open: boolean;
  newItineraryStarted: boolean;
  rider: OrganizationRiderDetail;
  uzurvRider: Rider;
  uzurvRiderFullName: string;
  organization: Organization;
  organizationRiderAddress: Address;
  organizationRiderDetails: Array<OrganizationRiderDetail>;
  vehicleTypes: Array<VehicleType>;
  isWavEnabled: boolean;
  isOnDemandWavEnabled: boolean;
  isWavWillCallEnabled: boolean;
  amenities: Array<Amenity>;
  rideRequestTypes: Array<RideRequestType>;
  riderPaymentAccounts: Array<PaymentAccount>;
  riderFavoriteLocations: Array<FavoriteLocation>;
  loadingRiderDetails: boolean;
  loadingRiderFavoriteLocations: boolean;
  organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>;
  rideLimitDetail: RideLimitDetail;
  rideMetadataFields: Array<RideMetadataField>;
  organizationServiceLevels: Array<OrganizationServiceLevel>;
  searchString: string;
  searchingRider: boolean;
  autocompleteResults: Array<OrganizationRiderListItem>;
  searchStringNewItinerary: string;
  searchingRiderNewItinerary: boolean;
  autocompleteResultsNewItinerary: Array<OrganizationRiderListItem>;
  addingNewRider: boolean;
  itineraryDate: Date;
  itineraryLegs: Array<RideRequest>;
  returnLegCreated: boolean;
  selectedVehicleType: VehicleType;
  selectedServiceLevelType: ServiceLevelType;
  selectedAmenitites: Array<Amenity>;
  selectedPaymentAccount: PaymentAccount;
  addingNewPaymentAccount: boolean;
  partnerIdentifier: string;
  itinerary: Itinerary;
  rideSubscription: RideSubscriptionDetail;
  submittingItinerary: boolean;
  riderPageSize: number;
  organizations: Array<Organization>;
  ignoredDrivers: Array<Driver>;
  favoriteDrivers: Array<Driver>;
  organizationPaymentMethods: Array<OrganizationPaymentMethod>;
  selectedOrganizationPaymentMethod: OrganizationPaymentMethod;
  cardTypes: Array<CardType>;
  newPaymentAccount: PaymentAccount;
  outstandingCharges: Array<ReservationChargeDetail>;
  payments: Array<Payment>;
  paymentHistoryPageSize: number;
  paymentHistoryPageIndex: number;
  expandedPayment: Payment;
  expandedPaymentTransactions: Array<Transaction>;
  loadingTransactions: boolean;
  createdOrganizationRiderSuccessfully: boolean;
  submittingPaymentAccount: boolean;
  customerServiceNotes: Array<CustomerServiceNoteDetail>;
  sendingUzurvRideAppInvitation: boolean;
  availableProviders: Array<Provider>;
  preferredProviderId: number;
  shouldShowPreferredProviderSelection;
  shouldShowPaymentSelection: boolean;
  isRideSubscription: boolean;
  selectedDaysOfTheWeek: Array<string>;
  subscriptionStartDate: Date;
  subscriptionEndDate: Date;
  selectedRiderPhoneNumber: string;
  trips: Array<Trip>;
  tripsPageIndex: number;
  tripsPageSize: number;
  selectedTabIndex: number;
}

export const initialState: OrganizationRiderState = {
  open: false,
  newItineraryStarted: false,
  rider: null,
  uzurvRider: null,
  uzurvRiderFullName: null,
  organizationRiderDetails: [],
  organization: null,
  organizationRiderAddress: null,
  vehicleTypes: [],
  isWavEnabled: false,
  isOnDemandWavEnabled: false,
  isWavWillCallEnabled: false,
  amenities: [],
  rideRequestTypes: [],
  riderPaymentAccounts: [],
  riderFavoriteLocations: [],
  loadingRiderDetails: false,
  loadingRiderFavoriteLocations: false,
  organizationRiderServiceLevels: [],
  rideLimitDetail: null,
  rideMetadataFields: [],
  organizationServiceLevels: [],
  searchString: '',
  searchingRider: false,
  autocompleteResults: [],
  searchStringNewItinerary: '',
  searchingRiderNewItinerary: false,
  autocompleteResultsNewItinerary: [],
  addingNewRider: false,
  itineraryDate: null,
  itineraryLegs: [],
  returnLegCreated: false,
  selectedVehicleType: null,
  selectedServiceLevelType: null,
  selectedAmenitites: null,
  selectedPaymentAccount: null,
  addingNewPaymentAccount: false,
  newPaymentAccount: null,
  partnerIdentifier: null,
  itinerary: null,
  rideSubscription: null,
  submittingItinerary: false,
  riderPageSize: 10,
  organizations: null,
  ignoredDrivers: null,
  favoriteDrivers: null,
  organizationPaymentMethods: [],
  selectedOrganizationPaymentMethod: null,
  cardTypes: null,
  outstandingCharges: null,
  payments: null,
  paymentHistoryPageSize: 20,
  paymentHistoryPageIndex: 0,
  expandedPayment: null,
  expandedPaymentTransactions: null,
  loadingTransactions: false,
  createdOrganizationRiderSuccessfully: false,
  submittingPaymentAccount: false,
  customerServiceNotes: [],
  sendingUzurvRideAppInvitation: false,
  availableProviders: [],
  shouldShowPreferredProviderSelection: false,
  shouldShowPaymentSelection: false,
  preferredProviderId: null,
  isRideSubscription: false,
  selectedDaysOfTheWeek: [],
  subscriptionStartDate: null,
  subscriptionEndDate: null,
  selectedRiderPhoneNumber: null,
  trips: [],
  tripsPageIndex: 0,
  tripsPageSize: 10,
  selectedTabIndex: 0
};

export const organizationRiderReducer = createReducer(
  initialState,

  on(OrganizationRiderActions.RestoreState, (state, action) => {
    return {
      ...action.state
    };
  }),

  on(OrganizationRiderActions.Start, (state, action) => {
    if (isNullOrUndefined(state)) {
      return {
        ...initialState,
        open: true,
        itineraryLegs: [new RideRequest()],
        newItineraryStarted: true
      };
    } else {
      return {
        ...state,
        // If the new itinerary is already started keep the rider, otherwise clear it. This is for tab restore
        rider: state.newItineraryStarted ? state.rider : null,
        uzurvRider: state.newItineraryStarted ? state.uzurvRider : null,
        itineraryLegs: state.newItineraryStarted ? state.itineraryLegs : [new RideRequest()],
        selectedRiderPhoneNumber: state.newItineraryStarted ? state.selectedRiderPhoneNumber : null,
        newItineraryStarted: true
      };
    }
  }),

  on(OrganizationRiderActions.Close, (state, action) => {
    return {
      ...(action.payload && action.payload.done ? initialState : state),
      open: false
    };
  }),

  on(OrganizationRiderActions.Resume, (state, action) => {
    return {
      ...state,
      open: true
    };
  }),

  on(OrganizationRiderActions.GetVehicleTypesSuccess, (state, action) => {
    return {
      ...state,
      vehicleTypes: action.vehicleTypes
    };
  }),

  on(OrganizationRiderActions.GetOrganizationRiderAddressSuccess, (state, action) => {
    return {
      ...state,
      organizationRiderAddress: action.address
    };
  }),

  on(OrganizationRiderActions.GetAmenitiesSuccess, (state, action) => {
    return {
      ...state,
      amenities: action.amenities
    };
  }),

  on(OrganizationRiderActions.GetRideRequestTypesSuccess, (state, action) => {
    return {
      ...state,
      rideRequestTypes: action.rideRequestTypes
    };
  }),

  on(OrganizationRiderActions.GetRideMetadataFieldsSuccess, (state, action) => {
    return {
      ...state,
      rideMetadataFields: action.rideMetadataFields
    };
  }),

  on(OrganizationRiderActions.SelectAmenities, (state, action) => {
    return {
      ...state,
      selectedAmenitites: action.amenities
    };
  }),

  on(OrganizationRiderActions.GetRiderPaymentAccountsSuccess, (state, action) => {
    return {
      ...state,
      riderPaymentAccounts: action.paymentAccounts
    };
  }),

  on(OrganizationRiderActions.GetRideLimitsSuccess, (state, action) => {
    return {
      ...state,
      rideLimitDetail: action.rideLimitDetail
    };
  }),

  on(OrganizationRiderActions.GetRiderFavoriteLocationsSuccess, (state, action) => {
    let sortedFavoriteLocations: Array<FavoriteLocation>;
    if (isNullOrUndefined(action.favoriteLocations)) {
      sortedFavoriteLocations = [];
    } else {
      sortedFavoriteLocations = action.favoriteLocations.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
    }

    return {
      ...state,
      riderFavoriteLocations: sortedFavoriteLocations,
      loadingRiderFavoriteLocations: false
    };
  }),

  on(OrganizationRiderActions.FetchAvailableProvidersSuccess, (state, action) => {
    let combinedProviderArray = [...state.availableProviders, ...action.availableProviders];

    combinedProviderArray = combinedProviderArray.filter(
      (value, index, array) => index === array.findIndex(object => object.id === value.id)
    );

    return {
      ...state,
      availableProviders: combinedProviderArray
    };
  }),

  on(OrganizationRiderActions.SelectPreferredProvider, (state, action) => {
    // A preferredProviderId of -1 means 'No Preference'. Map a value of -1 for preferredProviderId to null.
    const preferredProviderId = action.preferredProviderId === -1 ? null : action.preferredProviderId;
    const itineraryLegs = state.itineraryLegs;

    itineraryLegs.forEach(leg => {
      leg.preferredProviderId = preferredProviderId;
    });

    return {
      ...state,
      itineraryLegs: itineraryLegs,
      preferredProviderId: action.preferredProviderId
    };
  }),

  on(OrganizationRiderActions.ShouldShowPreferredProviderSelection, (state, action) => {
    return {
      ...state,
      shouldShowPreferredProviderSelection: action.shouldShowPreferredProviderSelection
    };
  }),

  on(OrganizationRiderActions.ShouldShowPaymentSelection, (state, action) => {
    return {
      ...state,
      shouldShowPaymentSelection: action.shouldShowPaymentSelection
    };
  }),

  on(OrganizationRiderActions.AddFavoriteLocationSuccess, (state, action) => {
    const favoriteLocations = cloneDeep(state.riderFavoriteLocations);

    const existingIndex = findIndex(favoriteLocations, favoriteLocation => {
      // TODO: Switch this back to case insensitive once the backend is updated to match
      // return favoriteLocation.label.toLowerCase() === action.payload.label.toLowerCase();
      return favoriteLocation.label === action.favoriteLocation.label;
    });

    if (existingIndex === -1) {
      favoriteLocations.push(action.favoriteLocation);
    } else {
      favoriteLocations.splice(existingIndex, 1, action.favoriteLocation);
    }

    return {
      ...state,
      riderFavoriteLocations: favoriteLocations
    };
  }),

  on(OrganizationRiderActions.DeleteFavoriteLocationSuccess, (state, action) => {
    const filteredFavoriteLocations: Array<FavoriteLocation> = state.riderFavoriteLocations.filter(
      favoriteLocation => favoriteLocation.label !== action.favoriteLocation.label
    );
    return {
      ...state,
      riderFavoriteLocations: filteredFavoriteLocations
    };
  }),

  on(OrganizationRiderActions.GetOrganizationRiderServiceLevelsSuccess, (state, action) => {
    return {
      ...state,
      organizationRiderServiceLevels: action.organizationRiderServiceLevels
    };
  }),

  on(OrganizationRiderActions.DeletePaymentAccountSuccess, (state, action) => {
    return {
      ...state,
      selectedPaymentAccount: null
    };
  }),

  on(OrganizationRiderActions.GetOrganizationServiceLevelsSuccess, (state, action) => {
    const nAD2DOrganizationServiceLevel = action.organizationServiceLevels?.find(
      osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
    );

    return {
      ...state,
      organizationServiceLevels: action.organizationServiceLevels,
      isWavEnabled: !isNullOrUndefined(nAD2DOrganizationServiceLevel),
      isOnDemandWavEnabled: nAD2DOrganizationServiceLevel?.rideNowEnabled,
      isWavWillCallEnabled: nAD2DOrganizationServiceLevel?.willCallEnabled
    };
  }),

  on(OrganizationRiderActions.SearchRider, (state, action) => {
    return {
      ...state,
      searchString: action.searchString,
      searchingRider: true
    };
  }),

  on(OrganizationRiderActions.RiderSearchSuccess, (state, action) => {
    return {
      ...state,
      searchingRider: false,
      autocompleteResults: action.organizationRiderListItems
    };
  }),

  on(OrganizationRiderActions.RiderSearchFail, state => {
    return {
      ...state,
      searchingRider: false
    };
  }),

  on(OrganizationRiderActions.SearchRiderNewItinerary, (state, action) => {
    return {
      ...state,
      searchStringNewItinerary: action.searchString,
      searchingRiderNewItinerary: true
    };
  }),

  on(OrganizationRiderActions.SearchRiderNewItinerarySuccess, (state, action) => {
    return {
      ...state,
      searchingRiderNewItinerary: false,
      autocompleteResultsNewItinerary: action.organizationRiderListItems
    };
  }),

  on(OrganizationRiderActions.SearchRiderNewItineraryFail, state => {
    return {
      ...state,
      searchingRiderNewItinerary: false
    };
  }),

  on(OrganizationRiderActions.AddNewRider, (state, action) => {
    return {
      ...state,
      addingNewRider: action.payload
    };
  }),

  on(OrganizationRiderActions.CancelAddNewRider, state => {
    return {
      ...state,
      addingNewRider: false,
      autocompleteResults: [],
      searchString: ''
    };
  }),

  on(OrganizationRiderActions.CreateOrganizationRiderSuccess, (state, action) => {
    return {
      ...state,
      addingNewRider: false,
      searchString: '',
      searchingRider: false,
      autocompleteResults: [],
      searchStringNewItinerary: '',
      searchingRiderNewItinerary: false,
      autocompleteResultsNewItinerary: [],
      createdOrganizationRiderSuccessfully: true,
      rider: action.payload.forNewItinerary
        ? action.payload.organizationRiderDetail
        : action.payload.forAddingOrganizationRider
        ? state.rider
        : null,
      selectedRiderPhoneNumber: initialState.selectedRiderPhoneNumber,
      organizationRiderDetails: action.payload.forAddingOrganizationRider
        ? [...state.organizationRiderDetails, action.payload.organizationRiderDetail].sort(
            (a: OrganizationRiderDetail, b: OrganizationRiderDetail) => {
              if (a.organizationId === state.rider.organizationId) return -1;
              if (b.organizationId === state.rider.organizationId) return 1;
              return a.organizationName.localeCompare(b.organizationName);
            }
          )
        : state.organizationRiderDetails
    };
  }),

  on(OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevelsSuccess, (state, action) => {
    return {
      ...state,
      addingNewRider: false,
      searchString: '',
      searchingRider: false,
      autocompleteResults: [],
      searchStringNewItinerary: '',
      searchingRiderNewItinerary: false,
      autocompleteResultsNewItinerary: [],
      createdOrganizationRiderSuccessfully: true
    };
  }),

  on(OrganizationRiderActions.ClearRiderSearch, state => {
    return {
      ...state,
      searchString: '',
      searchStringNewItinerary: '',
      autocompleteResults: [],
      autocompleteResultsNewItinerary: []
    };
  }),

  on(OrganizationRiderActions.GetOrganizationRiderDetailSuccess, (state, action) => {
    // If the rider is the same, we are rehydrating the state, don't clear other fields
    if (state.rider && state.rider.riderId === action.payload.riderId) {
      return {
        ...state,
        rider: action.payload,
        loadingRiderFavoriteLocations: true
      };
    }

    return {
      ...state,
      searchString: '',
      autocompleteResults: [],
      rider: action.payload,
      itineraryDate: initialState.itineraryDate,
      itineraryLegs: [new RideRequest()],
      selectedRiderPhoneNumber: initialState.selectedRiderPhoneNumber,
      uzurvRider: initialState.uzurvRider,
      selectedVehicleType: initialState.selectedVehicleType,
      selectedServiceLevelType: initialState.selectedServiceLevelType,
      selectedPaymentAccount: initialState.selectedPaymentAccount
    };
  }),

  on(OrganizationRiderActions.GetOrganizationSuccess, (state, action) => {
    return {
      ...state,
      organization: action.organization
    };
  }),

  on(OrganizationRiderActions.SetIsRideSubscription, (state, action) => {
    return {
      ...state,
      isRideSubscription: action.isRideSubscription
    };
  }),

  on(OrganizationRiderActions.SelectDaysOfTheWeek, (state, action) => {
    return {
      ...state,
      selectedDaysOfTheWeek: action.daysOfTheWeek
    };
  }),

  on(OrganizationRiderActions.SelectSubscriptionStartDate, (state, action) => {
    return {
      ...state,
      subscriptionStartDate: action.subscriptionStartDate
    };
  }),

  on(OrganizationRiderActions.SelectSubscriptionEndDate, (state, action) => {
    return {
      ...state,
      subscriptionEndDate: action.subscriptionEndDate
    };
  }),

  on(OrganizationRiderActions.SelectDate, (state, action) => {
    return {
      ...state,
      itineraryDate: action.date
    };
  }),

  on(OrganizationRiderActions.SetPartnerIdentifier, (state, action) => {
    return {
      ...state,
      partnerIdentifier: action.payload
    };
  }),

  on(OrganizationRiderActions.SelectVehicleType, (state, action) => {
    const serviceLevelType =
      action.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV
        ? ServiceLevelType.NON_AMBULATORY
        : ServiceLevelType.AMBULATORY;

    return {
      ...state,
      selectedVehicleType: action.vehicleType,
      selectedServiceLevelType: serviceLevelType
    };
  }),

  on(OrganizationRiderActions.NewLeg, (state, action) => {
    const legs = state.itineraryLegs;
    const newLeg = new RideRequest();
    const addingReturnLeg = action.payload.isReturnLeg;
    newLeg.pickupLocation = legs[legs.length - 1].dropoffLocation;
    if (addingReturnLeg) {
      newLeg.dropoffLocation = legs[0].pickupLocation;
    }
    legs.push(newLeg);
    return {
      ...state,
      itineraryLegs: legs,
      returnLegCreated: addingReturnLeg
    };
  }),

  on(OrganizationRiderActions.RemoveLeg, (state, action) => {
    const legs = state.itineraryLegs;
    legs.splice(action.payload.index, 1);
    return {
      ...state,
      itineraryLegs: legs,
      // if (the last leg is being deleted and a return leg was created), or if there was never a return leg, no return leg should exist.
      // otherwise, a return leg was created, and it wasn't deleted, so leave returnLegCreated true
      returnLegCreated: (action.payload.isLast && state.returnLegCreated) || !state.returnLegCreated ? false : true
    };
  }),

  on(OrganizationRiderActions.UpdateLeg, (state, action) => {
    state.itineraryLegs[action.payload.index] = action.payload.leg;
    return state;
  }),

  on(OrganizationRiderActions.ResetAllLegs, state => {
    return {
      ...state,
      itineraryLegs: [new RideRequest()]
    };
  }),

  on(OrganizationRiderActions.SelectOrganizationPaymentMethod, (state, action) => {
    return {
      ...state,
      selectedOrganizationPaymentMethod: action.organizationPaymentMethod
    };
  }),

  on(OrganizationRiderActions.SelectPaymentAccount, (state, action) => {
    return {
      ...state,
      selectedPaymentAccount: action.paymentAccount
    };
  }),

  on(OrganizationRiderActions.AddPaymentAccount, state => {
    return {
      ...state,
      addingNewPaymentAccount: true,
      newPaymentAccount: new PaymentAccount()
    };
  }),

  on(OrganizationRiderActions.AddPaymentAccountSuccess, (state, action) => {
    return {
      ...state,
      addingNewPaymentAccount: false,
      selectedPaymentAccount: action.paymentAccount.verified ? action.paymentAccount : null
    };
  }),

  on(OrganizationRiderActions.CancelAddPaymentAccount, state => {
    return {
      ...state,
      addingNewPaymentAccount: false,
      newPaymentAccount: new PaymentAccount()
    };
  }),

  on(OrganizationRiderActions.CreatePayment, state => {
    return {
      ...state,
      submittingPaymentAccount: true
    };
  }),

  on(OrganizationRiderActions.CreatePaymentSuccess, state => {
    return {
      ...state,
      submittingPaymentAccount: false
    };
  }),

  on(OrganizationRiderActions.CreatePaymentFail, state => {
    return {
      ...state,
      submittingPaymentAccount: false
    };
  }),

  on(OrganizationRiderActions.EditPaymentAccount, state => {
    return {
      ...state,
      submittingPaymentAccount: true
    };
  }),

  on(OrganizationRiderActions.EditPaymentAccountSuccess, state => {
    return {
      ...state,
      submittingPaymentAccount: false
    };
  }),

  on(OrganizationRiderActions.EditPaymentAccountFail, state => {
    return {
      ...state,
      submittingPaymentAccount: false
    };
  }),

  on(OrganizationRiderActions.ItineraryValidated, (state, action) => {
    const itinerary = new Itinerary();

    // The backend only needs the ID, but expects an Organization Rider
    const rider = new OrganizationRider();
    rider.id = state.rider.organizationRiderId;

    itinerary.organizationRider = rider;
    itinerary.organization = state.organization;
    itinerary.partnerIdentifier = state.partnerIdentifier;
    itinerary.rideRequests = state.itineraryLegs;
    itinerary.pickupDate = state.itineraryDate;
    itinerary.riderPaymentAccountId = state.selectedPaymentAccount ? state.selectedPaymentAccount.id : null;
    itinerary.amenities = state.selectedAmenitites;

    let paymentCollectionType: PaymentCollectionType = null;

    if (!isNullOrUndefined(state.organizationPaymentMethods) && !isNullOrUndefined(state.selectedOrganizationPaymentMethod)) {
      state.organizationPaymentMethods.forEach(opm => {
        if (opm.paymentMethod.paymentCollectionType.code === state.selectedOrganizationPaymentMethod.toString()) {
          paymentCollectionType = opm.paymentMethod.paymentCollectionType;
        }
      });
    }

    itinerary.rideRequests.forEach(ride => {
      ride.riderPaymentCollectionType = paymentCollectionType;
      ride.riderPhoneNumber = state.selectedRiderPhoneNumber;
    });

    return {
      ...state,
      itinerary: itinerary
    };
  }),

  on(OrganizationRiderActions.SubmitItinerary, (state, action) => {
    return {
      ...state,
      itineraryLegs: state.itineraryLegs.map(rideRequest =>
        RideUtil.updateRideRequestServiceLevel(rideRequest, state.selectedServiceLevelType, state.organizationServiceLevels)
      ),
      submittingItinerary: true
    };
  }),

  on(OrganizationRiderActions.AbandonSubmission, state => {
    return {
      ...state,
      submittingItinerary: false
    };
  }),

  on(OrganizationRiderActions.SubmitItinerarySuccess, (_, action) => {
    return {
      ...initialState,
      itinerary: action.itinerary
    };
  }),

  on(OrganizationRiderActions.SubmitItineraryFail, state => {
    return {
      ...state,
      submittingItinerary: false
    };
  }),

  on(OrganizationRiderActions.SubmitRideSubscriptionSuccess, (_, action) => {
    return {
      ...initialState,
      rideSubscription: action.rideSubscriptionDetail
    };
  }),

  on(OrganizationRiderActions.SubmitRideSubscriptionFail, state => {
    return {
      ...state,
      submittingItinerary: false
    };
  }),

  on(OrganizationRiderActions.ShowMoreResults, (state, action) => {
    return {
      ...state,
      riderPageSize: (state.riderPageSize += 10)
    };
  }),

  on(OrganizationRiderActions.GetOrganizationsSuccess, (state, action) => {
    return {
      ...state,
      organizations: action.organizations
    };
  }),

  on(OrganizationRiderActions.GetPaymentMethodsSuccess, (state, action) => {
    return {
      ...state,
      organizationPaymentMethods: action.organizationPaymentMethods
    };
  }),

  on(OrganizationRiderActions.GetCardTypesSuccess, (state, action) => {
    return {
      ...state,
      cardTypes: action.cardTypes
    };
  }),

  on(OrganizationRiderActions.CreatePaymentAccount, (state, action) => {
    return {
      ...state,
      submittingPaymentAccount: true
    };
  }),

  on(OrganizationRiderActions.CreatePaymentAccountSuccess, (state, action) => {
    return {
      ...state,
      newPaymentAccount: new PaymentAccount(),
      addingNewPaymentAccount: false,
      submittingPaymentAccount: false,
      selectedPaymentAccount: action.paymentAccount
    };
  }),

  on(OrganizationRiderActions.CreatePaymentAccountFail, (state, action) => {
    return {
      ...state,
      submittingPaymentAccount: false
    };
  }),

  on(OrganizationRiderActions.GetOutstandingChargesSuccess, (state, action) => {
    return {
      ...state,
      outstandingCharges: action.outstandingCharges
    };
  }),

  on(OrganizationRiderActions.SetNewItineraryStarted, (state, action) => {
    return {
      ...state,
      newItineraryStarted: action.isNewItinerary
    };
  }),

  on(OrganizationRiderActions.GetPaymentsSuccess, (state, action) => {
    return {
      ...state,
      payments: action.payments
    };
  }),

  on(OrganizationRiderActions.PagePaymentsList, (state, action) => {
    return {
      ...state,
      paymentHistoryPageIndex: action.pageEvent.pageIndex,
      paymentHistoryPageSize: action.pageEvent.pageSize
    };
  }),

  on(OrganizationRiderActions.ExpandPayment, (state, action) => {
    state.rider.paymentAccounts.forEach((paymentAccount: PaymentAccount) => {
      if (action.payment.paymentAccount.id === paymentAccount.id) {
        action.payment.paymentAccount = paymentAccount;
        return;
      }
    });

    return {
      ...state,
      expandedPayment: action.payment === state.expandedPayment ? null : action.payment,
      expandedPaymentTransactions: null
    };
  }),

  on(OrganizationRiderActions.GetExpandedPaymentTransactions, (state, action) => {
    return {
      ...state,
      loadingTransactions: true
    };
  }),

  on(OrganizationRiderActions.GetExpandedPaymentTransactionsSuccess, (state, action) => {
    return {
      ...state,
      expandedPaymentTransactions: action.transactions,
      loadingTransactions: false
    };
  }),

  on(OrganizationRiderActions.GetExpandedPaymentTransactionsFail, (state, action) => {
    return {
      ...state,
      loadingTransactions: false
    };
  }),

  on(OrganizationRiderActions.SelectRider, (state, action) => {
    return {
      ...state,
      searchString: '',
      searchingRider: false,
      autocompleteResults: [],
      searchStringNewItinerary: '',
      searchingRiderNewItinerary: false,
      autocompleteResultsNewItinerary: []
    };
  }),

  on(OrganizationRiderActions.GetNotesSuccess, (state, action) => {
    return {
      ...state,
      customerServiceNotes: action.payload
    };
  }),

  on(OrganizationRiderActions.SendUzurvRideAppInvitation, state => {
    return {
      ...state,
      sendingUzurvRideAppInvitation: true
    };
  }),

  on(OrganizationRiderActions.SendUzurvRideAppInvitationSuccess, state => {
    return {
      ...state,
      sendingUzurvRideAppInvitation: false
    };
  }),

  on(OrganizationRiderActions.SendUzurvRideAppInvitationFail, state => {
    return {
      ...state,
      sendingUzurvRideAppInvitation: false
    };
  }),

  on(OrganizationRiderActions.AbandonSendUzurvRideAppInvitation, state => {
    return {
      ...state,
      sendingUzurvRideAppInvitation: false
    };
  }),

  on(OrganizationRiderActions.GetRiderProfileSuccess, (state, action) => {
    return {
      ...state,
      loadingRiderDetails: false,
      uzurvRider: action.riderProfile.rider,
      uzurvRiderFullName: action.riderProfile.firstName + ' ' + action.riderProfile.lastName,
      organizationRiderAddress: isNullOrUndefined(action.riderProfile.address)
        ? state.organizationRiderAddress
        : action.riderProfile.address,
      organizationRiderDetails: action.riderProfile.organizationRiderDetails.sort(
        (a: OrganizationRiderDetail, b: OrganizationRiderDetail) => {
          if (a.organizationId === state.rider.organizationId) return -1;
          if (b.organizationId === state.rider.organizationId) return 1;
          return a.organizationName.localeCompare(b.organizationName);
        }
      ),
      favoriteDrivers: action.riderProfile.favoriteDrivers,
      ignoredDrivers: action.riderProfile.ignoredDrivers
    };
  }),

  on(OrganizationRiderActions.GetRiderProfileFail, (state, action) => {
    return {
      ...state,
      loadingRiderDetails: false
    };
  }),

  on(OrganizationRiderActions.UpdateUzurvRiderSuccess, (state, action) => {
    return {
      ...state,
      uzurvRider: action.payload.rider,
      uzurvRiderFullName: action.payload.rider.firstName + ' ' + action.payload.rider.lastName,
      organizationRiderAddress: isNullOrUndefined(action.payload.organizationRiderAddress)
        ? state.organizationRiderAddress
        : action.payload.organizationRiderAddress
    };
  }),

  on(OrganizationRiderActions.UpdateOrganizationRiderEligibilitySuccess, (state, action) => {
    return {
      ...state,
      organizationRiderDetails: state.organizationRiderDetails.map(organizationRiderDetail => {
        if (organizationRiderDetail.organizationRiderId === action.payload.organizationRiderDetail.organizationRiderId) {
          let clonedOrganizationRiderDetail = cloneDeep(action.payload.organizationRiderDetail);
          clonedOrganizationRiderDetail.eligible = action.payload.isRiderEligible;
          return clonedOrganizationRiderDetail;
        }

        return organizationRiderDetail;
      })
    };
  }),

  on(OrganizationRiderActions.ModifyOrganizationRiderDetailSuccess, (state, action) => {
    return {
      ...state,
      organizationRiderDetails: state.organizationRiderDetails.map(organizationRiderDetail => {
        if (organizationRiderDetail.organizationRiderId === action.organizationRiderDetail.organizationRiderId) {
          organizationRiderDetail = cloneDeep(action.organizationRiderDetail);
        }
        return organizationRiderDetail;
      })
    };
  }),

  on(OrganizationRiderActions.GetOrganizationRiderDetailSuccess, state => {
    return {
      ...state,
      loadingRiderDetails: true
    };
  }),

  on(OrganizationRiderActions.RemoveIgnoredDriverSuccess, (state, action) => {
    return {
      ...state,
      ignoredDrivers: state.ignoredDrivers.filter(driver => driver.id !== action.payload.driver.id)
    };
  }),

  on(OrganizationRiderActions.SelectRiderPhoneNumber, (state, action) => {
    return {
      ...state,
      selectedRiderPhoneNumber: action.selectedRiderPhoneNumber
    };
  }),

  on(OrganizationRiderActions.GetRiderTripsSuccess, (state, action) => {
    return {
      ...state,
      trips: action.trips
    };
  }),

  on(OrganizationRiderActions.TripsPagination, (state, action) => {
    return {
      ...state,
      tripsPageIndex: action.pageEvent.pageIndex,
      tripsPageSize: action.pageEvent.pageSize
    };
  }),

  on(OrganizationRiderActions.SelectTabIndex, (state, action) => {
    return {
      ...state,
      selectedTabIndex: action.selectedTabIndex
    };
  })
);
